import React, { useEffect, useRef, createRef, isValidElement } from 'react';
import CBAccordion from '@cb/apricot/CBAccordion';
import { usePrevious } from '@cb/react-hooks';
import cn from 'classnames';
import { isBoolean, isUndefined, isString, isNumber } from 'lodash';
import { AccordionControls } from './AccordionControls';
/**
 * Previous value reference
 * @param value - value to keep reference of the previous
 */
/**
 * AccordionList component
 */
export const AccordionList = ({ children, title, controls, single = false, onOpenChange, onChange, resizeOnOpen, autoUpdate = true, animation = true, heightAdjustment = 0, className, cbRef, }) => {
    const ref = useRef(null);
    const [childRefs, setChildRefs] = React.useState([]);
    const prevChildren = usePrevious(children); // save the previous children
    const accordion = useRef(null);
    const onPanelOpenChangeCallBack = panelEl => {
        var _a;
        const isOpen = panelEl.classList.contains('cb-in');
        // if open and should be resized - force accordion resize
        if (isOpen && resizeOnOpen && accordion.current) {
            accordion.current.adjustHeight();
        }
        const index = childRefs.findIndex(child => {
            return child && child.querySelector('.cb-accordion-panel') === panelEl;
        });
        if (children && isValidElement(children[index])) {
            if ((_a = children[index].props) === null || _a === void 0 ? void 0 : _a.onOpenChange) {
                children[index].props.onOpenChange(isOpen);
            }
            if (onOpenChange) {
                onOpenChange(index, isOpen);
            }
        }
    };
    const onChangeCallBack = panelEl => {
        var _a;
        const isOpen = panelEl.classList.contains('cb-in');
        const index = childRefs.findIndex(child => {
            return child && child.querySelector('.cb-accordion-panel') === panelEl;
        });
        if (children && isValidElement(children[index])) {
            if ((_a = children[index].props) === null || _a === void 0 ? void 0 : _a.onChange) {
                children[index].props.onChange(isOpen);
            }
            if (onChange) {
                onChange(index, isOpen);
            }
        }
    };
    /**
     * Check if the major child props changed
     *  - check the children length first
     *  - then all child props that are strings and numbers (shallow comparison)
     *    to avoid complex comparisons
     */
    const majorChildPropsChanged = () => {
        const currentChildren = React.Children.toArray(children);
        const previousChildren = React.Children.toArray(prevChildren);
        if (currentChildren.length !== previousChildren.length) {
            return true;
        }
        let isChanged = false;
        currentChildren.forEach((current, index) => {
            const previous = previousChildren[index];
            if (current && previous && isValidElement(current) && isValidElement(previous)) {
                isChanged =
                    isChanged ||
                        Object.keys(current.props).some(key => 
                        // compare only simple types here
                        (isString(current.props[key]) ||
                            isNumber(current.props[key]) ||
                            isBoolean(current.props[key])) &&
                            current.props[key] !== previous.props[key]);
            }
        });
        return isChanged;
    };
    useEffect(() => {
        if (isUndefined(prevChildren) || autoUpdate || majorChildPropsChanged()) {
            if (accordion.current) {
                accordion.current.destroy();
            }
            accordion.current = CBAccordion({
                elem: ref.current,
                multiOpen: !single,
                animation,
                heightAdjustment,
                callBack: onPanelOpenChangeCallBack,
                onChange: onChangeCallBack,
            });
            // if we have an external ref object - update it
            if (cbRef) {
                // eslint-disable-next-line no-param-reassign
                cbRef.current = accordion.current;
            }
        }
    }, [children, autoUpdate]);
    /**
     * When we have children - create an array of refs
     */
    React.useEffect(() => {
        // add or remove refs
        setChildRefs(new Array(React.Children.toArray(children).length)
            .fill(undefined)
            .map((_, i) => childRefs[i] || createRef()));
    }, [children]);
    /**
     * Fill the refs array with child references
     */
    const extendedChildren = () => {
        return React.Children.map(children, (child, index) => {
            if (isValidElement(child)) {
                return React.cloneElement(child, {
                    ref: r => {
                        if (r) {
                            childRefs[index] = r;
                        }
                    },
                });
            }
            return child;
        });
    };
    return (React.createElement("div", { className: cn('cb-accordion', className), ref: ref, "data-cbtrack-comp": "apricot-react:accordion" },
        React.createElement(AccordionControls, { title: title, controls: controls }),
        React.createElement("div", { className: "cb-accordion-panels" }, extendedChildren())));
};
//# sourceMappingURL=AccordionList.js.map